import { CanAbortRequests, HasLoading, StoreBase } from "../../store/storeBase";
import { runInAction } from "mobx";
import { useEffect, useRef } from "react";
import { RequestHandle, abortRequest, createRequestHandle } from "wl-net";

export const createRequestControl = () => {
  const cur: Set<RequestHandle> = new Set();

  const createRequestHandle_ = () => {
    const h = createRequestHandle();
    cur.add(h);
    return h;
  };

  const abortAllRequests = () => {
    for (const h of [...cur]) {
      abortRequest(h);
    }
    cur.clear();
  };

  return {
    createRequestHandle: createRequestHandle_,
    abortAllRequests,
  };
};

export type RequestControl = ReturnType<typeof createRequestControl>;

export const createStore = <T extends HasLoading>(
  constr: (rc: RequestControl) => T,
  rc?: RequestControl
): T & CanAbortRequests => {
  if (!rc) rc = createRequestControl();
  const r = constr(rc) as T & CanAbortRequests;
  if (!r.abortRequests) r.abortRequests = rc.abortAllRequests;
  return r;
};

export const createStoreAsync = async <T extends HasLoading>(
  constr: (rc: RequestControl) => Promise<void | T>,
  rc?: RequestControl
): Promise<void | (T & CanAbortRequests)> => {
  if (!rc) rc = createRequestControl();

  const r = await constr(rc);

  if (!r) {
    return;
  }

  const result = r as T & CanAbortRequests;
  if (!result.abortRequests) {
    result.abortRequests = rc.abortAllRequests;
  }

  return result;
};

export const useWatchNetwork = (...stores: (StoreBase | undefined)[]) => {
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      // В Strict Mode cleanup вызывается дважды - игнорируем первый вызов
      if (mountedRef.current) {
        mountedRef.current = false;
        return;
      }
      for (const store of stores) {
  	    store?.abortRequests();
      }
    };
  }, []);
};

export const withLoading = async <T>(
  self: HasLoading,
  p: Promise<T>
): Promise<T> => {
  self.isLoading = true;
  let r: T | undefined = undefined;
  try {
    r = await p;
  } finally {
    runInAction(() => {
      self.isLoading = false;
    });
  }
  return r;
};
